import { useMemo } from 'react';

import type { WCollectionTileView } from '@zola/svc-web-api-ts-client';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { HeadingElement, HeadingElementType } from '@zola/zola-ui/src/typography/Headings';

import type { CamelCasedPropertiesDeep } from 'type-fest';

import styles from './collectionGroupTile.module.less';

type SampleImage = {
  title: string;
  imageUrl: string;
};

export const CollectionGroupTile = ({
  tile,
  titleComponent = 'h2',
  lazy,
}: {
  titleComponent?: HeadingElementType;
  tile: CamelCasedPropertiesDeep<WCollectionTileView>;
  lazy?: boolean;
}): JSX.Element => {
  const sampleImages: SampleImage[] = useMemo(() => {
    const samples = (tile.sampleItems || [])
      .map((item) => {
        const imageUrl = item.image?.aspectRatios?.['1X1']?.small ?? null;
        if (imageUrl) {
          return {
            title: item.name,
            imageUrl,
          } as SampleImage;
        }
        return null;
      })
      .filter((image) => image !== null) as SampleImage[];
    return samples.slice(0, 6);
  }, [tile.sampleItems]);

  return (
    <LinkV2
      className={styles.tile}
      noUnderline
      href={`/shop/collection/${tile.key}`}
      title={tile.description}
    >
      <div className={styles.cover}>
        <div className={styles.coverImageContainer}>
          <img
            className={styles.coverImage}
            loading={lazy ? 'lazy' : undefined}
            src={tile.coverImagePath}
            alt={tile.title}
          />
        </div>
        <HeadingElement
          component={titleComponent}
          className={styles.title}
          presentation="h5"
          strong
        >
          {tile.title}
        </HeadingElement>
      </div>
      <div className={styles.previews}>
        {sampleImages.map((image, index) => (
          <img
            loading={lazy ? 'lazy' : undefined}
            src={image.imageUrl}
            alt={image.title}
            key={index}
          />
        ))}
      </div>
    </LinkV2>
  );
};
